/**
 * Connexion et inscription.
 *
 * C'est le premier écran qu'un prospect voit, et souvent le seul avant de décider s'il
 * poursuit. Il porte donc deux charges : ouvrir la porte sans friction, et dire en trois
 * secondes ce que fait le produit — d'où le panneau de gauche, qui montre une carte de
 * contrôle plutôt que d'en parler.
 *
 * Trois règles de lisibilité, apprises d'une première version ratée :
 *
 * 1. **Le panneau de marque ne suit pas le thème.** Il était peint avec la couleur
 *    d'accent, qui s'éclaircit en mode sombre pour rester visible sur du charbon —
 *    résultat, un aplat bleu vif portant un texte presque noir, délavé et fatigant. Une
 *    identité de marque n'a aucune raison de changer de couleur avec les préférences
 *    d'affichage : ce panneau est un fond profond fixe, avec du texte clair dessus.
 * 2. **Un champ doit se voir avant d'être rempli.** Une surface à peine plus claire que
 *    la page, bordée d'un trait de séparation, ne se distingue pas d'un vide. Les champs
 *    ont donc leur propre teinte et une bordure appuyée.
 * 3. **Un bouton désactivé reste lisible** (voir `Button` dans `ui/primitives`).
 *
 * Deux choix de fond, hérités du serveur et assumés jusque dans l'affichage :
 *
 * - **On ne dit jamais si un compte existe.** Ni à l'inscription (« déjà pris »), ni à la
 *   connexion (« mot de passe incorrect » plutôt que « compte inconnu »). Distinguer les
 *   deux livrerait la liste des clients de nos clients à quiconque essaie des adresses.
 * - **S'inscrire crée une organisation**, pas un compte isolé. Un compte sans
 *   organisation ne verrait rien et n'aurait rien à faire ; les membres suivants arrivent
 *   par invitation d'un administrateur.
 */

import {
  AlertCircle,
  ArrowRight,
  Check,
  Eye,
  EyeOff,
  Loader2,
  Moon,
  Sun,
} from "lucide-react";
import { useId, useState, type FormEvent, type ReactNode } from "react";

import { Logo } from "./AppShell";
import {
  AuthError,
  MIN_PASSWORD_LENGTH,
  type Identity,
  login,
  register,
} from "./authApi";
import { cn } from "./ui/primitives";
import { useSession } from "./ui/session";
import { useTheme } from "./ui/theme";

type Mode = "login" | "register";

/** Fond du panneau de marque : constant, quel que soit le thème choisi. */
const ENCRE = "#0B1622";
const ENCRE_HAUTE = "#13314f";

/**
 * Une carte de contrôle réduite à son geste : des points, une moyenne, deux limites, et
 * un point qui sort — en rouge, parce que c'est le seul endroit de l'écran où la couleur
 * porte une information.
 *
 * Décorative, donc `aria-hidden` : un lecteur d'écran n'a rien à gagner à l'entendre
 * décrire, et l'écran est déjà annoncé par son titre.
 */
function ChartMotif() {
  const points = [
    52, 44, 58, 47, 61, 40, 55, 49, 66, 43, 57, 51, 45, 60, 48, 54, 41, 59, 50,
    14,
  ];
  const x = (i: number) => 6 + (i * 288) / (points.length - 1);
  const hors = (v: number) => v < 22 || v > 78;

  return (
    <svg viewBox="0 0 300 100" className="w-full" aria-hidden="true">
      {[22, 78].map((y) => (
        <line
          key={y}
          x1="0"
          y1={y}
          x2="300"
          y2={y}
          strokeWidth="0.7"
          strokeDasharray="5 4"
          stroke="rgb(255 255 255 / 0.28)"
        />
      ))}
      <line
        x1="0"
        y1="50"
        x2="300"
        y2="50"
        strokeWidth="0.7"
        stroke="rgb(255 255 255 / 0.16)"
      />
      <polyline
        points={points.map((v, i) => `${String(x(i))},${String(v)}`).join(" ")}
        fill="none"
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
        stroke="rgb(255 255 255 / 0.65)"
      />
      {points.map((v, i) =>
        hors(v) ? (
          <g key={i}>
            <circle cx={x(i)} cy={v} r="7" fill="rgb(248 113 113 / 0.22)" />
            <circle cx={x(i)} cy={v} r="3.2" fill="#f87171" />
          </g>
        ) : (
          <circle
            key={i}
            cx={x(i)}
            cy={v}
            r="1.9"
            fill="rgb(255 255 255 / 0.75)"
          />
        ),
      )}
    </svg>
  );
}

const ARGUMENTS = [
  "Lit vos exports machine tels qu'ils sortent — CSV, Excel, en-têtes bancals compris",
  "Cartes de contrôle, règles de Nelson, Cp/Cpk/Pp/Ppk",
  "Détecte l'autocorrélation avant de tracer des limites fausses",
];

function PanneauMarque() {
  return (
    <aside
      className="relative hidden w-[44%] max-w-2xl shrink-0 flex-col justify-between overflow-hidden p-12 lg:flex"
      style={{
        // Un dégradé plutôt qu'un aplat : l'aplat de couleur pleine page est ce qui
        // faisait « site web des années 2010 ». Le halo décentré donne la profondeur
        // sans qu'aucune image ne soit chargée.
        backgroundColor: ENCRE,
        backgroundImage: `radial-gradient(120% 90% at 15% 0%, ${ENCRE_HAUTE} 0%, transparent 62%),
           linear-gradient(180deg, rgb(255 255 255 / 0.04) 0%, transparent 40%)`,
      }}
    >
      {/* Trame fine : la texture d'un papier millimétré, à peine perceptible. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(rgb(255 255 255 / 0.035) 1px, transparent 1px), linear-gradient(90deg, rgb(255 255 255 / 0.035) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
          maskImage:
            "radial-gradient(80% 70% at 30% 30%, black 0%, transparent 100%)",
        }}
      />

      <div className="relative flex items-center gap-3 text-white">
        <Logo className="h-8 w-8" />
        <div className="leading-tight">
          <div className="text-[1.05rem] font-semibold tracking-tight">
            Capalys
          </div>
          <div className="text-[0.72rem] text-white/55">
            Maîtrise statistique des procédés
          </div>
        </div>
      </div>

      <div className="relative">
        <h1 className="max-w-md text-[2.1rem] leading-[1.12] font-semibold tracking-tight text-white">
          Vos exports machine,
          <br />
          lus tels qu'ils sortent.
        </h1>
        <ul className="mt-7 flex max-w-md flex-col gap-3">
          {ARGUMENTS.map((texte) => (
            <li
              key={texte}
              className="flex gap-3 text-[0.875rem] leading-snug text-white/75"
            >
              <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-white/10">
                <Check className="h-2.5 w-2.5 text-white/80" />
              </span>
              {texte}
            </li>
          ))}
        </ul>
      </div>

      <div className="relative">
        <div className="mb-3 flex items-center gap-2 text-[0.66rem] tracking-[0.14em] text-white/50 uppercase">
          <span className="h-px w-6 bg-white/25" />
          Carte de contrôle — un point hors limite
        </div>
        <ChartMotif />
      </div>
    </aside>
  );
}

/**
 * Champ de saisie de l'écran d'authentification.
 *
 * Volontairement distinct de l'`Input` du reste de l'application : celui-ci est calibré
 * pour des tableaux denses (28 px de haut), ce qui est juste dans une grille de mesures
 * et faux dans un formulaire qu'on remplit au clavier.
 */
function Champ({
  label,
  aide,
  suffixe,
  ...rest
}: React.InputHTMLAttributes<HTMLInputElement> & {
  label: ReactNode;
  aide?: ReactNode;
  suffixe?: ReactNode;
}) {
  const id = useId();

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-[0.8rem] font-medium text-ink">
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          className={cn(
            "h-11 w-full rounded-lg px-3.5 text-[0.9rem] text-ink",
            // Teinte propre et bordure appuyée : un champ vide doit se voir comme un
            // champ, pas comme un trou dans la page.
            "border border-field bg-surface-2",
            "placeholder:text-faint",
            "transition-[border-color,box-shadow] outline-none",
            "hover:border-accent/50",
            "focus:border-accent focus:ring-2 focus:ring-accent/25",
            "disabled:cursor-not-allowed disabled:text-faint",
            suffixe && "pr-11",
          )}
          {...rest}
        />
        {suffixe && (
          <div className="absolute inset-y-0 right-1.5 flex items-center">
            {suffixe}
          </div>
        )}
      </div>
      {aide}
    </div>
  );
}

/** Sélecteur segmenté : les deux chemins sont visibles d'emblée, sans lien à chercher. */
function Segments({
  mode,
  onChange,
  disabled,
}: {
  mode: Mode;
  onChange: (m: Mode) => void;
  disabled: boolean;
}) {
  const options: { id: Mode; label: string }[] = [
    { id: "login", label: "Se connecter" },
    { id: "register", label: "Créer une organisation" },
  ];

  return (
    <div className="mb-7 grid grid-cols-2 gap-1 rounded-lg border border-line bg-surface-2 p-1">
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          disabled={disabled}
          onClick={() => {
            onChange(option.id);
          }}
          className={cn(
            "rounded-md px-3 py-2 text-[0.82rem] font-medium transition-colors",
            "disabled:cursor-not-allowed",
            mode === option.id
              ? "bg-surface text-ink shadow-sm"
              : "text-muted hover:text-ink",
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function AuthScreen() {
  const { adopt } = useSession();
  const { mode: theme, toggle } = useTheme();

  const [mode, setMode] = useState<Mode>("login");
  const [organization, setOrganization] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [visible, setVisible] = useState(false);
  const [erreur, setErreur] = useState<AuthError | null>(null);
  const [enCours, setEnCours] = useState(false);

  const basculer = (next: Mode) => {
    setMode(next);
    setErreur(null);
    setPassword("");
    setVisible(false);
  };

  async function soumettre(event: FormEvent) {
    event.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      const identity: Identity =
        mode === "login"
          ? await login(email.trim(), password)
          : await register({
              organization: organization.trim(),
              email: email.trim(),
              password,
              fullName,
            });
      adopt(identity);
    } catch (cause) {
      setErreur(
        cause instanceof AuthError
          ? cause
          : new AuthError(
              "Le service est injoignable. Vérifiez votre connexion, puis réessayez.",
              0,
              null,
            ),
      );
      setEnCours(false);
    }
  }

  const trop_court =
    mode === "register" &&
    password.length > 0 &&
    password.length < MIN_PASSWORD_LENGTH;

  const pret =
    email.trim().length > 0 &&
    password.length > 0 &&
    (mode === "login" ||
      (organization.trim().length >= 2 &&
        password.length >= MIN_PASSWORD_LENGTH));

  return (
    <div className="flex min-h-screen bg-bg text-ink">
      <PanneauMarque />

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center justify-between px-5 py-4 lg:justify-end">
          <div className="flex items-center gap-2 lg:hidden">
            <Logo className="h-6 w-6" />
            <span className="text-[0.95rem] font-semibold">Capalys</span>
          </div>
          <button
            onClick={toggle}
            className="rounded-lg border border-line p-2 text-muted transition-colors hover:bg-surface-2 hover:text-ink"
            aria-label={theme === "dark" ? "Thème clair" : "Thème sombre"}
          >
            {theme === "dark" ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
          </button>
        </div>

        <div className="flex flex-1 items-center justify-center px-5 pb-12">
          <div className="w-full max-w-[26.5rem] animate-[rise_0.35s_cubic-bezier(0.16,1,0.3,1)_both]">
            {/* Une carte, et non un formulaire posé sur le vide : elle donne un bord au
                regard et sépare la saisie du fond. */}
            <div className="rounded-xl border border-line bg-surface p-7 shadow-[0_1px_2px_rgb(var(--c-shadow)/var(--shadow-strength)),0_12px_28px_-12px_rgb(var(--c-shadow)/var(--shadow-strength))]">
              <Segments mode={mode} onChange={basculer} disabled={enCours} />

              <h2 className="text-[1.4rem] font-semibold tracking-tight">
                {mode === "login" ? "Connexion" : "Créer une organisation"}
              </h2>
              <p className="mt-1.5 text-[0.84rem] leading-snug text-muted">
                {mode === "login"
                  ? "Accédez à vos caractéristiques et à vos analyses."
                  : "Le premier compte est administrateur : il pourra ensuite inviter ses ingénieurs et techniciens."}
              </p>

              <form onSubmit={soumettre} className="mt-6 flex flex-col gap-4">
                {mode === "register" && (
                  <>
                    <Champ
                      label="Organisation"
                      placeholder="Verrerie de l'Atlas"
                      autoComplete="organization"
                      value={organization}
                      onChange={(e) => {
                        setOrganization(e.target.value);
                      }}
                      disabled={enCours}
                      required
                    />
                    <Champ
                      label={
                        <>
                          Votre nom{" "}
                          <span className="font-normal text-faint">
                            (facultatif)
                          </span>
                        </>
                      }
                      autoComplete="name"
                      value={fullName}
                      onChange={(e) => {
                        setFullName(e.target.value);
                      }}
                      disabled={enCours}
                    />
                  </>
                )}

                <Champ
                  label="Adresse électronique"
                  type="email"
                  placeholder="prenom.nom@entreprise.com"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                  }}
                  disabled={enCours}
                  required
                />

                <Champ
                  label="Mot de passe"
                  type={visible ? "text" : "password"}
                  autoComplete={
                    mode === "login" ? "current-password" : "new-password"
                  }
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                  }}
                  disabled={enCours}
                  required
                  suffixe={
                    <button
                      type="button"
                      onClick={() => {
                        setVisible((v) => !v);
                      }}
                      className="rounded-md p-2 text-faint transition-colors hover:text-ink"
                      aria-label={
                        visible
                          ? "Masquer le mot de passe"
                          : "Afficher le mot de passe"
                      }
                    >
                      {visible ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  }
                  aide={
                    mode === "register" ? (
                      // La contrainte est annoncée avant l'envoi, jamais après :
                      // découvrir une règle par un refus est la façon la plus sûre de
                      // faire abandonner quelqu'un.
                      <span
                        className={cn(
                          "text-[0.74rem] leading-snug",
                          trop_court ? "text-bad" : "text-muted",
                        )}
                      >
                        {MIN_PASSWORD_LENGTH} caractères minimum. Une phrase
                        dont vous vous souvenez vaut mieux qu'un mot compliqué.
                      </span>
                    ) : undefined
                  }
                />

                {erreur && (
                  <div
                    role="alert"
                    className="flex gap-2.5 rounded-lg border border-bad/25 bg-bad-soft px-3.5 py-3 text-[0.82rem] leading-snug text-bad"
                  >
                    <AlertCircle className="mt-px h-4 w-4 shrink-0" />
                    <span>
                      {erreur.message}
                      {erreur.retryAfter !== null && (
                        <>
                          {" "}
                          Réessayez dans {erreur.retryAfter} seconde
                          {erreur.retryAfter > 1 ? "s" : ""}.
                        </>
                      )}
                    </span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!pret || enCours}
                  className={cn(
                    "mt-1 inline-flex h-11 w-full items-center justify-center gap-2",
                    "rounded-lg text-[0.9rem] font-semibold",
                    "bg-accent text-accent-ink",
                    "transition-[filter,background-color] hover:brightness-110",
                    "focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:outline-none",
                    // Désactivé mais lisible : le contraste baisse, le texte reste.
                    "disabled:cursor-not-allowed disabled:border disabled:border-line",
                    "disabled:bg-surface-2 disabled:text-muted disabled:brightness-100",
                  )}
                >
                  {enCours ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {mode === "login" ? "Connexion…" : "Création…"}
                    </>
                  ) : (
                    <>
                      {mode === "login"
                        ? "Se connecter"
                        : "Créer l'organisation"}
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              </form>
            </div>

            {mode === "register" && (
              <p className="mt-4 px-1 text-[0.78rem] leading-snug text-muted">
                Votre organisation existe déjà ? Demandez une invitation à votre
                administrateur plutôt que d'en créer une seconde — les données
                ne seraient pas partagées.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
